import React, { useState } from "react";
import { useNavigate, useLocation, Routes, Route, Outlet } from "react-router-dom";
import DefaultEntry from "../components/entry/DefaultEntry.jsx";
import MilkEntries from "../components/entry/MilkEntries.jsx";
import UserList from "../components/reusable/usersList.jsx";
import FatPrices from "../components/reusable/FatInputs.jsx";
import CowPriceEntryGrid from "../components/reusable/CowPriceEntryGrid.jsx";
import { AnimatePresence, motion } from "framer-motion";
import { RefreshCw, UsersRound, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageHeader from "../components/reusable/PageHeader.jsx";
import NoCustomerState from "../components/entry/NoCustomerModal.jsx";

function Entry({ role }) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState("");
  const [Name, setUserName] = useState("");
  const [customerStatus, setCustomerStatus] = useState(
    role === "admin" ? "loading" : "ready",
  );
  const [userListKey, setUserListKey] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();

  const PAGE_TITLES = [
    { match: "/milk-entry", title: "Milk Entry" },
    { match: "/ghee-entry", title: "Ghee Entry" },
    { match: "/feed-entry", title: "Feed Entry" },
    { match: "/receive-money", title: "Receive Money" },
    { match: "/give-money", title: "Give Money" },
  ];

  const pageTitle =
    PAGE_TITLES.find((p) => location.pathname.includes(p.match))?.title ||
    "Milk Entries";

  const handleUserDataLoaded = (users) => {
    if (role !== "admin" || !Array.isArray(users)) return;
    setCustomerStatus(users.length === 0 ? "empty" : "ready");
  };

  const retryCustomers = () => {
    setCustomerStatus("loading");
    setUserListKey((key) => key + 1);
  };

  const canShowEntryForm =
    role !== "admin" || (customerStatus === "ready" && Boolean(userId));

  const showCustomerSelector =
    role === "admin" &&
    (customerStatus === "loading" || customerStatus === "ready");

  const handleCustomerSelect = (selectedId) => {
    setUserId(selectedId);
    if (selectedId) {
      window.setTimeout(() => {
        document.getElementById("entry-form")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 120);
    }
  };

  return (
    <div className="relative mx-auto min-h-screen w-full max-w-lg bg-paper pb-28 font-sans text-ink">
      <main className="px-4 pt-5">
        <PageHeader title={pageTitle} />

        {customerStatus === "empty" && role === "admin" && (
          <NoCustomerState
            onBack={() => navigate("/")}
            onAddCustomer={() => {
              navigate("/addCustomer");
            }}
          />
        )}

        {customerStatus === "error" && role === "admin" && (
          <section className="app-card px-5 py-7 text-center ring-1 ring-black/[0.035]">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-debit/[0.08] text-debit">
              <WifiOff className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-[18px] font-extrabold text-ink">
              {t("entrySetup.load_error_title")}
            </h2>
            <p className="mt-2 text-[13px] leading-6 text-muted">
              {t("entrySetup.load_error_body")}
            </p>
            <button
              type="button"
              onClick={retryCustomers}
              className="app-primary-button mt-5 inline-flex items-center justify-center gap-2 text-[13px]"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {t("entrySetup.retry")}
            </button>
          </section>
        )}

        {showCustomerSelector && (
          <div className="app-card mb-4 p-4">
            <UserList
              key={userListKey}
              onSelectUser={handleCustomerSelect}
              onUserChange={setUserName}
              onDataLoaded={handleUserDataLoaded}
              onLoadError={() => setCustomerStatus("error")}
            />
          </div>
        )}

        {role === "admin" && customerStatus === "ready" && !userId && (
          <div className="rounded-[22px] border border-dashed border-primary/20 bg-primary/[0.035] px-5 py-6 text-center">
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-primary shadow-sm">
              <UsersRound className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 className="mt-3 text-[14px] font-extrabold text-ink">
              {t("entrySetup.select_title")}
            </h2>
            <p className="mx-auto mt-1.5 max-w-[300px] text-[12px] leading-5 text-muted">
              {t("entrySetup.select_body")}
            </p>
          </div>
        )}

        {canShowEntryForm && (
          <div id="entry-form" className="scroll-mt-5">
            <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.15 }}
            >
              <Routes location={location}>
                <Route
                  path="*"
                  element={
                    <MilkEntries userId={userId} role={role} Name={Name} />
                  }
                />
                <Route
                  path="/feed-entry"
                  element={
                    <DefaultEntry
                      userId={userId}
                      userSelectedOption="Feed"
                      role={role}
                      Name={Name}
                    />
                  }
                />
                <Route
                  path="/ghee-entry"
                  element={
                    <DefaultEntry
                      userId={userId}
                      userSelectedOption="Ghee"
                      role={role}
                      Name={Name}
                    />
                  }
                />
                <Route
                  path="/receive-money"
                  element={
                    <DefaultEntry
                      userId={userId}
                      userSelectedOption="Money Received"
                      role={role}
                      Name={Name}
                    />
                  }
                />
                <Route
                  path="/give-money"
                  element={
                    <DefaultEntry
                      userId={userId}
                      userSelectedOption="Money Given"
                      role={role}
                      Name={Name}
                    />
                  }
                />
                <Route
                  path="/buffalo/prices"
                  element={<FatPrices tableTitle="Buffalo" />}
                />
                <Route
                  path="/cow/prices"
                  element={<FatPrices tableTitle="Cow" />}
                />
                <Route
                  path="/buffalo/prices/snf"
                  element={<CowPriceEntryGrid tableTitle="Buffalo-snf" />}
                />
                <Route
                  path="/cow/prices/snf"
                  element={<CowPriceEntryGrid tableTitle="Cow-snf" />}
                />
              </Routes>
            </motion.div>
            </AnimatePresence>
          </div>
        )}

        <Outlet />
      </main>
    </div>
  );
}

export default Entry;
